import { useEffect, useState, useTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark.jsx';
import PasswordField from '../components/PasswordField.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { api } from '../lib/api.js';
import { clearMfaPending, readMfaPending } from '../lib/mfaChallenge.js';
import { seedProfileFromProvider } from '../lib/settingsStore.js';
import loginHeroSrc from '../assets/brand/login-hero.png';
import {
  PRIVACY_URL,
  PRIVACY_VERSION,
  TERMS_URL,
  TERMS_VERSION,
  assertTermsAccepted,
} from '../lib/legalTerms.js';

function routeForRole(role) {
  if (role === 'ATTORNEY') return '/attorney';
  if (role === 'ADMIN') return '/admin';
  return '/client/home';
}

export default function LoginPage() {
  const { login, signup, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('signin'); // signin | signup
  const [role, setRole] = useState('CLIENT');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [licensedStates, setLicensedStates] = useState('CA,TX');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [pending, startTransition] = useTransition();

  const isSignup = mode === 'signup';
  const resetToken = String(searchParams.get('reset') || '');
  const isReset = Boolean(resetToken);

  useEffect(() => {
    const modeParam = String(searchParams.get('mode') || '').toLowerCase();
    if (modeParam === 'signup' || modeParam === 'register') {
      setMode('signup');
    } else if (modeParam === 'signin' || modeParam === 'login') {
      setMode('signin');
    }
    const emailParam = String(searchParams.get('email') || '').trim();
    if (emailParam.includes('@')) setEmail(emailParam);
    const nameParam = String(searchParams.get('name') || '').trim();
    if (nameParam) setDisplayName(nameParam);
    if (searchParams.get('mfa') === '1') {
      const pendingMfa = readMfaPending();
      if (pendingMfa?.challenge) {
        setMfa(pendingMfa);
        if (pendingMfa.email) setEmail(pendingMfa.email);
        if (pendingMfa.remember === false) setRemember(false);
      }
    }
  }, [searchParams]);

  function switchMode(next) {
    setMode(next);
    setError('');
    setInfo('');
    setConfirmPassword('');
    setMfa(null);
    setMfaCode('');
    clearMfaPending();
    if (next !== 'signup') setAcceptedTerms(false);
  }

  function leaveMfa() {
    setMfa(null);
    setMfaCode('');
    clearMfaPending();
    setError('');
    setInfo('');
  }

  function onForgotPassword(e) {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Enter the email you use to sign in, then choose Forgot Your Password?');
      return;
    }
    startTransition(async () => {
      try {
        const data = await api.post('/server/auth_handler/password/forgot', { email });
        setInfo(
          data?.message ||
            'If an account matches that email, we sent a link to change the password. A saved recovery email receives the link.'
        );
      } catch (err) {
        if (err.status === 404 || err.code === 'NOT_FOUND') {
          setError('Password reset is not on the published API yet. Publish the DemandJet API, then try again.');
          return;
        }
        if (err.code === 'EMAIL_NOT_CONFIGURED') {
          setInfo(err.message);
          return;
        }
        setError(err.message || 'Unable to send a password reset.');
      }
    });
  }

  function onSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');

    startTransition(async () => {
      try {
        if (isReset) {
          if (password.length < 8) {
            throw new Error('Password must be at least 8 characters.');
          }
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          const data = await api.post('/server/auth_handler/password/reset', {
            token: resetToken,
            password,
          });
          const signedIn = await login({
            email: data.email || email,
            password,
            role,
            remember,
          });
          if (signedIn?.mfaRequired) {
            setMfa({ ...signedIn, email: data.email || email, remember });
            setMfaCode('');
            return;
          }
          navigate(routeForRole(signedIn.role));
          return;
        }

        if (isSignup) {
          if (password !== confirmPassword) {
            throw new Error('Passwords do not match');
          }
          const terms = assertTermsAccepted({
            acceptedTerms,
            termsVersion: TERMS_VERSION,
            privacyVersion: PRIVACY_VERSION,
          });
          const session = await signup({
            email,
            password,
            displayName: displayName.trim() || email.split('@')[0],
            role,
            licensedStates: role === 'ATTORNEY' ? licensedStates : '',
            remember,
            acceptedTerms: terms.acceptedTerms,
            termsVersion: terms.termsVersion,
            privacyVersion: terms.privacyVersion,
          });
          navigate(routeForRole(session.role));
          return;
        }

        if (mfa?.challenge) {
          const session = await verifyMfa({
            challenge: mfa.challenge,
            code: mfaCode,
            remember: mfa.remember !== false && remember,
          });
          if (mfa.profileHints || mfa.provider) {
            seedProfileFromProvider(session.email, {
              ...(mfa.profileHints || {}),
              displayName: mfa.profileHints?.displayName || mfa.displayName || session.displayName,
              email: session.email,
              source: mfa.provider || mfa.profileHints?.source || 'social',
            });
          }
          clearMfaPending();
          navigate(routeForRole(session.role));
          return;
        }

        const signedIn = await login({ email, password, role, remember });
        if (signedIn?.mfaRequired) {
          const pendingMfa = { ...signedIn, email, remember };
          setMfa(pendingMfa);
          setMfaCode('');
          return;
        }
        navigate(routeForRole(signedIn.role));
      } catch (err) {
        if (isReset && (err.status === 404 || err.code === 'NOT_FOUND')) {
          setError('Password reset is not on the published API yet. Publish the DemandJet API, then try again.');
          return;
        }
        setError(
          err.message ||
            (isReset ? 'Unable to save the new password.' : isSignup ? 'Unable to create account' : 'Unable to sign in')
        );
      }
    });
  }

  return (
    <div className="login-page">
      <div className="login-page-bg" aria-hidden="true">
        <img className="login-page-bg-image" src={loginHeroSrc} alt="" />
        <div className="login-page-bg-veil" />
      </div>

      <div className="login-page-center">
        <form className="login-card" onSubmit={onSubmit}>
          <div className="login-card-brand">
            <BrandMark size={40} />
          </div>

          <h1 className="login-title">
            {mfa
              ? 'Enter your sign-in code'
              : isReset
                ? 'Choose a new password'
                : isSignup
                  ? 'Create your DemandJet account'
                  : 'DemandJet login'}
          </h1>
          <p className="login-subtitle">
            {mfa
              ? mfa.method === 'SMS'
                ? `Enter the 6-digit code sent to ${mfa.hint || 'your recovery phone'}.`
                : 'Enter the 6-digit code from your authenticator app.'
              : isReset
                ? 'This link sets a new password for your account.'
                : isSignup
                  ? 'Set up your workspace to start routing attorney demand letters.'
                  : 'Sign in to continue to your DemandJet workspace.'}
          </p>

          {mfa ? (
            <label className="field">
              <span>Authentication code</span>
              <input
                className="mfa-code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
              />
            </label>
          ) : null}

          {!mfa && !isReset ? (
          <>
          <div
            className="role-toggle"
            role="tablist"
            aria-label={isSignup ? 'Register as' : 'Sign in as'}
          >
            <button
              type="button"
              role="tab"
              aria-selected={role === 'CLIENT'}
              className={role === 'CLIENT' ? 'active' : ''}
              onClick={() => setRole('CLIENT')}
            >
              Client
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === 'ATTORNEY'}
              className={role === 'ATTORNEY' ? 'active' : ''}
              onClick={() => setRole('ATTORNEY')}
            >
              Attorney Partner
            </button>
          </div>

          {isSignup ? (
            <label className="field">
              <span>Full name</span>
              <input
                type="text"
                autoComplete="name"
                placeholder="Alex Morgan"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>
          ) : null}

          <label className="field">
            <span>Username</span>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          </>
          ) : null}

          {!mfa ? (
          <>
          <PasswordField
            label={isReset ? 'New password' : 'Password'}
            required
            minLength={8}
            autoComplete={isSignup || isReset ? 'new-password' : 'current-password'}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {isSignup || isReset ? (
            <PasswordField
              label="Confirm password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          ) : null}

          {isSignup && role === 'ATTORNEY' ? (
            <label className="field">
              <span>Licensed states</span>
              <input
                type="text"
                required
                placeholder="CA,TX,NY"
                value={licensedStates}
                onChange={(e) => setLicensedStates(e.target.value.toUpperCase())}
              />
              <small className="field-hint">
                Comma-separated 2-letter codes used for jurisdiction routing.
              </small>
            </label>
          ) : null}

          {isSignup ? (
            <label className="checkbox login-terms">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
              />
              <span>
                I agree to the{' '}
                <a
                  className="text-link"
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  className="text-link"
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          ) : !isReset ? (
            <div className="login-row">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className="text-link link-button"
                onClick={onForgotPassword}
                disabled={pending}
              >
                Forgot Your Password?
              </button>
            </div>
          ) : null}
          </>
          ) : null}

          <button
            className="btn-primary login-submit"
            type="submit"
            disabled={pending || (isSignup && !acceptedTerms) || (mfa && mfaCode.length !== 6)}
          >
            {pending
              ? mfa
                ? 'Checking…'
                : isReset
                  ? 'Saving…'
                  : isSignup
                    ? 'Creating account…'
                    : 'Signing in…'
              : mfa
                ? 'Verify code'
                : isReset
                  ? 'Save password'
                  : isSignup
                    ? 'Create Account'
                    : 'Log In'}
          </button>

          {error ? <p className="form-error">{error}</p> : null}
          {info ? <p className="form-info">{info}</p> : null}

          <p className="login-footer">
            {mfa ? (
              <button type="button" className="text-link strong link-button" onClick={leaveMfa}>
                Use password sign-in
              </button>
            ) : isReset ? (
              <button
                type="button"
                className="text-link strong link-button"
                onClick={() => navigate('/login')}
              >
                Back to sign in
              </button>
            ) : isSignup ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  className="text-link strong link-button"
                  onClick={() => switchMode('signin')}
                >
                  Sign In
                </button>
              </>
            ) : (
              <>
                Not a client yet?{' '}
                <button
                  type="button"
                  className="text-link strong link-button"
                  onClick={() => switchMode('signup')}
                >
                  Create an Account
                </button>
              </>
            )}
          </p>

          {!isSignup ? (
            <p className="login-legal">
              <a
                className="text-link"
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy
              </a>
              <span className="login-legal-sep" aria-hidden="true">
                ·
              </span>
              <a
                className="text-link"
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
